/**
 * Blocos compartilhados das páginas MOBILE (Task #31 Fase 2/3 + Fase 1 03/07).
 *
 * Mesmo padrão visual do MobileResumo: cards full-width, Stat label/valor,
 * header sticky com logout. READ-ONLY — nenhuma entrada de dados.
 *
 * Fase 1 (hardening UX 03/07):
 * - alvos de toque ≥44px (min-h-11) em Sair/Atualizar/nav;
 * - `active:` no lugar de `hover:` (touch não tem hover);
 * - legendas text-xs (11px era ilegível na palma);
 * - `MobileHeader` ganha ATUALIZAR (onRefresh) — invalida caches e remonta;
 * - `Card` ganha variante TOCÁVEL (onPress) com chevron — usada só quando o
 *   toque LEVA a algum lugar (nada de botão morto, doutrina #246);
 * - `ErroNote`: estado de ERRO explícito por card (distinto de carregando "…"
 *   e de sem-dado "—").
 */
import type { ReactNode } from "react";
import { ChevronRight, RefreshCw } from "lucide-react";

export type Tone = "pos" | "neg" | "turq" | "neutro";

export const TONE_CLS: Record<Tone, string> = {
  pos: "text-indra-positivo",
  neg: "text-indra-negativo",
  turq: "text-indra-turquesa",
  neutro: "text-white",
};

export const MONEY = { decimals: 0 } as const;
/** Abreviado (mi/mil) — padrão dos KPIs mobile (consistente com o desktop). */
export const MONEY_ABBR = { abbreviate: true } as const;
export const sign = (v: number) => (v >= 0 ? "+" : "");

export function Stat({ label, value, tone = "neutro", small = false, testId }: {
  label: string; value: string; tone?: Tone; small?: boolean; testId?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-xs tracking-wider text-white/60">{label}</span>
      <span
        className={`${small ? "text-lg" : "text-2xl"} font-black font-sans whitespace-nowrap tabular-nums ${TONE_CLS[tone]}`}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

export function Card({ title, children, testId, onPress, pressHint }: {
  title: string;
  children: ReactNode;
  testId: string;
  /** Toque leva a algum lugar (troca de aba etc). SEM onPress = card estático
   * read-only — nunca prometer drill que não existe. */
  onPress?: () => void;
  /** Rótulo curto do destino (a11y + visual), ex. "abrir Fluxo". */
  pressHint?: string;
}) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-indra-turquesa text-xs tracking-wider font-semibold">{title}</h2>
        {onPress && (
          <span className="flex items-center gap-0.5 text-white/40 text-[11px]">
            {pressHint}
            <ChevronRight size={14} />
          </span>
        )}
      </div>
      {children}
    </>
  );
  if (onPress) {
    return (
      <button
        type="button"
        onClick={onPress}
        aria-label={pressHint ? `${title} — ${pressHint}` : title}
        className="w-full text-left rounded-2xl border border-white/10 bg-indra-azul-surface p-5 active:border-indra-turquesa/40 active:bg-indra-azul-surface/80 focus:outline-none focus:ring-2 focus:ring-indra-turquesa/40"
        data-testid={testId}
      >
        {inner}
      </button>
    );
  }
  return (
    <section
      className="rounded-2xl border border-white/10 bg-indra-azul-surface p-5"
      data-testid={testId}
    >
      {inner}
    </section>
  );
}

/** Estado de ERRO por card — explícito e distinto de "—" (sem dado). */
export function ErroNote({ testId }: { testId?: string }) {
  return (
    <div
      className="text-xs text-amber-300/90 bg-amber-400/10 border border-amber-300/30 rounded-md px-2.5 py-1.5 mt-1"
      data-testid={testId ?? "mobile-erro"}
    >
      Falha ao carregar — toque em atualizar (↻) no topo.
    </div>
  );
}

/** Header sticky padrão (Cortex + subtítulo + Atualizar + Sair). */
export function MobileHeader({ subtitle, onLogout, onRefresh }: {
  subtitle: string;
  onLogout: () => void;
  /** Recarrega os dados da casca (invalida caches + remonta a página). */
  onRefresh?: () => void;
}) {
  return (
    <header className="sticky top-0 z-10 flex items-center justify-between px-5 py-2 bg-indra-azul/95 backdrop-blur border-b border-white/10">
      <div>
        <div className="text-white font-black text-lg leading-none">Cortex</div>
        <div className="text-white/60 text-xs tracking-wider">Indra Energia · {subtitle}</div>
      </div>
      <div className="flex items-center gap-2">
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            data-testid="mobile-refresh"
            aria-label="Atualizar dados"
            className="min-h-11 min-w-11 flex items-center justify-center text-white/60 active:text-white border border-white/10 rounded-md"
          >
            <RefreshCw size={16} />
          </button>
        )}
        <button
          type="button"
          onClick={onLogout}
          data-testid="mobile-logout"
          className="min-h-11 flex items-center text-xs font-semibold text-white/60 active:text-white border border-white/10 rounded-md px-3"
        >
          Sair
        </button>
      </div>
    </header>
  );
}

/** Aviso "melhor no desktop" pras views pesadas (conferência/Mesa/Histórico
 * denso/formulários) — NÃO tenta espremer a tabela no mobile. */
export function DesktopOnlyNotice({ titulo, descricao }: { titulo: string; descricao: string }) {
  return (
    <section
      className="rounded-2xl border border-white/10 bg-indra-azul-surface p-5 text-center"
      data-testid="mobile-desktop-only"
    >
      <h2 className="text-white font-semibold mb-1">{titulo}</h2>
      <p className="text-white/50 text-sm">{descricao}</p>
      <p className="text-white/40 text-xs mt-2">Melhor visualizado no desktop.</p>
    </section>
  );
}
